import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { equipesDoLider, filtrarPeloLider } from '../triagem/triagemPedidoUtils';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { depoisDoClique, manterNaTriagem, noBanco } from '../triagem/acaoTriagem';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';

export interface ItemTriagemErroSolar {
  item_id: string;
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  produto: string;
  quantidade: number;
  plano_valor: number;
  subtotal: number;
  numero_cotacao_operadora: string;
  entrada_coluna_em: string;
  /** Manter ou pendência externa: o pedido vai para o fim da fila. */
  manter_em: string | null;
  /** De onde a linha sai, para o histórico do clique (sem ir ao banco de novo). */
  quadro_atual: string;
  status_id: string;
  is_first_of_cnpj: boolean;
  /** 69a — o grupo visual é o PEDIDO: itens seguintes ficam indentados. */
  is_first_of_pedido: boolean;
  /** posição do item dentro do pedido (1-based), p/ "item N do pedido". */
  item_index: number;
}

/* O PEDIDO ANDA INTEIRO NA TRIAGEM (Douglas 24/10/2026, maquete aprovada): a
   linha é o pedido e todo botão leva as linhas dele nesta fila, num UPDATE só.
   Antes andava pela cotação e, sem cotação, só a linha clicada. */
export interface PedidoTriagemErroSolar {
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  tipo_pessoa: string | null;
  documento: string;
  razao_social: string;
  entrada_coluna_em: string;
  /** A vez do pedido na fila: a entrada, ou o último Manter / pendência externa. */
  relogio: string;
  cotacoes: string[];
  quantidade: number;
  valor: number;
  itens: ItemTriagemErroSolar[];
}

export function agruparErroSolarPorPedido(itens: ItemTriagemErroSolar[]): PedidoTriagemErroSolar[] {
  const grupos = new Map<string, PedidoTriagemErroSolar>();
  for (const i of itens) {
    let g = grupos.get(i.pedido_id);
    if (!g) {
      g = {
        pedido_id: i.pedido_id, numero_pedido: i.numero_pedido, cnpj: i.cnpj, tipo_pessoa: i.tipo_pessoa,
        documento: i.documento, razao_social: i.razao_social, entrada_coluna_em: i.entrada_coluna_em,
        relogio: '', cotacoes: [], quantidade: 0, valor: 0, itens: [],
      };
      grupos.set(i.pedido_id, g);
    }
    g.itens.push(i);
    g.quantidade += i.quantidade || 0;
    g.valor += i.subtotal || 0;
    if (i.entrada_coluna_em && (!g.entrada_coluna_em || i.entrada_coluna_em < g.entrada_coluna_em)) g.entrada_coluna_em = i.entrada_coluna_em;
    if (i.manter_em && i.manter_em > g.relogio) g.relogio = i.manter_em;
    const cot = i.numero_cotacao_operadora?.trim();
    if (cot && !g.cotacoes.includes(cot)) g.cotacoes.push(cot);
  }
  /* FIFO (Douglas 30/10/2026): a fila anda pela vez de cada pedido; o Manter e
     a pendência externa mandam o pedido para o fim. */
  const vez = (g: PedidoTriagemErroSolar) => (g.relogio > g.entrada_coluna_em ? g.relogio : g.entrada_coluna_em);
  const lista = Array.from(grupos.values());
  for (const g of lista) g.relogio = vez(g);
  return lista.sort((a, b) => (a.relogio < b.relogio ? -1 : a.relogio > b.relogio ? 1 : 0));
}

const COLUNA_ERRO_SOLAR = 'ERRO SOLAR  INPUT MANUAL';
const rotuloPedido = (n: number, numero: string) => (n > 1 ? `Pedido ${numero} (${n} linhas)` : `Pedido ${numero}`);

export function useTriagemErroSolar() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { moverPedidoJunto } = usePedidoMovement();
  const { profile } = useUserProfileContext();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  const { data: itens = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-erro-solar'],
    queryFn: async () => {
      const lider = await equipesDoLider(profile);
      const { data, error } = await filtrarPeloLider(supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, cnpj, razao_social, entrada_coluna_em, produto, manter_em, quantidade, plano_valor, subtotal, numero_cotacao_operadora, tipo_pessoa, documento, quadro_atual, status_id')
        .eq('quadro_atual', 'TRAMITACAO_VIVO')
        .eq('coluna_atual', 'ERRO SOLAR  INPUT MANUAL')
        .order('manter_em', { ascending: true, nullsFirst: true })
        .order('cnpj', { ascending: true })
        .order('entrada_coluna_em', { ascending: true }), profile, lider);

      if (error) {
        onQueryError(error);
        throw error;
      }

      onQuerySuccess();

      let lastCnpj = '';
      let lastPedido = '';
      let itemIdx = 0;
      type Linha = { subtotal?: number | null; numero_cotacao_operadora?: string | null; quadro_atual?: string | null; status_id?: string | null };
      const itensProcessados: ItemTriagemErroSolar[] = (data || []).map(item => {
        const extra = item as typeof item & Linha;
        const isFirst = item.cnpj !== lastCnpj;
        lastCnpj = item.cnpj || '';
        const isFirstPedido = item.pedido_id !== lastPedido;
        lastPedido = item.pedido_id || '';
        itemIdx = isFirstPedido ? 1 : itemIdx + 1;
        return {
          item_id: item.item_id,
          pedido_id: item.pedido_id,
          numero_pedido: String(item.numero_pedido || ''),
          cnpj: item.cnpj || '',
          tipo_pessoa: item.tipo_pessoa ?? null,
          documento: item.documento || item.cnpj || '',
          razao_social: item.razao_social || '',
          produto: item.produto || '',
          quantidade: Number(item.quantidade) || 0,
          plano_valor: Number(item.plano_valor) || 0,
          subtotal: Number(extra.subtotal) || (Number(item.plano_valor) * Number(item.quantidade || 1)),
          numero_cotacao_operadora: String(extra.numero_cotacao_operadora || ''),
          entrada_coluna_em: item.entrada_coluna_em || '',
          manter_em: item.manter_em || null,
          quadro_atual: extra.quadro_atual || 'TRAMITACAO_VIVO',
          status_id: extra.status_id || '',
          is_first_of_cnpj: isFirst,
          is_first_of_pedido: isFirstPedido,
          item_index: itemIdx,
        };
      });

      return itensProcessados;
    },
    refetchInterval,
  });

  /* A linha sai (ou desce) assim que o banco confirma; Kanban e Planilha só
     ficam marcados (`triagem/acaoTriagem.ts`). */
  const refletirClique = (pedidoId: string, modo: 'sai' | 'desce') => {
    void depoisDoClique(queryClient, ['triagem-erro-solar'], { pedidoId }, modo);
    queryClient.invalidateQueries({ queryKey: ['sla-input'] });
  };

  const pedidos = useMemo(() => agruparErroSolarPorPedido(itens), [itens]);

  const linhasDoPedido = (itemId: string) => {
    const clicado = itens.find(i => i.item_id === itemId);
    if (!clicado) throw new Error('Pedido não encontrado na fila. Atualize a triagem.');
    const linhas = itens.filter(i => i.pedido_id === clicado.pedido_id);
    return { clicado, linhas, ids: linhas.map(i => i.item_id) };
  };

  // Manter — o pedido inteiro vai para o final da fila
  const moverParaFinal = useMutation({
    mutationFn: async (itemId: string) => {
      const { clicado, ids } = linhasDoPedido(itemId);
      const r = await manterNaTriagem({ pedidoId: clicado.pedido_id, itens: ids, coluna: COLUNA_ERRO_SOLAR });
      return { count: r.mantidos, numero: clicado.numero_pedido, pedidoId: clicado.pedido_id };
    },
    onSuccess: ({ count, numero, pedidoId }) => {
      toast({ title: 'Fim da fila', description: `${rotuloPedido(count, numero)} foi para o fim da fila.` });
      refletirClique(pedidoId, 'desce');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  // Input Realizado — salva a cotação em todas as linhas do pedido e manda o
  // pedido inteiro para VALIDAÇÃO PENDENTE. Pedido que voltou do Erro de Input
  // já tem cotação: a nova SUBSTITUI a antiga.
  /* Uma ida só (`fn_erro_solar_input_realizado`): grava a cotação e manda o
     pedido para a Validação pendente na mesma transação. Se o pedido não puder
     andar, a cotação também não fica gravada. */
  const realizarInput = useMutation({
    mutationFn: async ({ itemId, numeroCotacao }: { itemId: string; numeroCotacao: string }) => {
      const { clicado, ids } = linhasDoPedido(itemId);
      const r = await noBanco<{ total: number; cotacao_anterior: string | null }>('fn_erro_solar_input_realizado', {
        p_pedido_id: clicado.pedido_id, p_itens: ids, p_cotacao: numeroCotacao,
      }, 'O input não foi registrado.');
      const anterior = (r.cotacao_anterior || '').trim();
      return {
        numeroCotacao, count: r.total, trocou: !!anterior && anterior !== numeroCotacao.trim(),
        numero: clicado.numero_pedido, pedidoId: clicado.pedido_id,
      };
    },
    onSuccess: ({ numeroCotacao, count, trocou, numero, pedidoId }) => {
      toast({ title: 'Input realizado', description: `Cotação ${numeroCotacao} ${trocou ? 'substituiu a anterior' : 'salva'}. ${rotuloPedido(count, numero)} foi para a Validação pendente.` });
      queryClient.invalidateQueries({ queryKey: ['triagem-cotacao'] });
      queryClient.invalidateQueries({ queryKey: ['triagem-fluxo'] });
      refletirClique(pedidoId, 'sai');
    },
    onError: (error) => {
      toast({ title: 'Erro ao realizar input', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  // TT RECURSOS INVÁLIDOS (Douglas 29/08) — o mesmo botão da triagem de Input
  // Realizado Móvel: cliente com débito muda de fila pra buscar a fatura. Sem
  // pedir valor da dívida (regra de 05/08: o valor só chega depois, pelo GC).
  // Leva o pedido inteiro.
  const moverParaTTRecursos = useMutation({
    mutationFn: async (itemId: string) => {
      const { clicado, ids } = linhasDoPedido(itemId);
      const r = await moverPedidoJunto({
        pedidoId: clicado.pedido_id,
        itensIds: ids,
        quadroOrigem: clicado.quadro_atual || 'TRAMITACAO_VIVO',
        colunaOrigem: COLUNA_ERRO_SOLAR,
        toQuadro: 'TRAMITACAO_VIVO',
        toColuna: '(TT) RECURSOS INVALIDOS',
        motivo: 'Crédito Negado - Dívida Interna',
        origemTriagem: true,
      });
      return { count: r.total, numero: clicado.numero_pedido, pedidoId: clicado.pedido_id };
    },
    onSuccess: ({ count, numero, pedidoId }) => {
      toast({
        title: 'Crédito negado',
        description: `${rotuloPedido(count, numero)} foi para TT Recursos Inválidos. O valor da dívida vem do GC.`,
        variant: 'destructive',
      });
      refletirClique(pedidoId, 'sai');
    },
    onError: (error) => {
      toast({ title: 'Erro ao mover pedido', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  return {
    itens,
    pedidos,
    isLoading,
    refetch,
    moverParaFinal: moverParaFinal.mutateAsync,
    realizarInput: realizarInput.mutateAsync,
    moverParaTTRecursos: moverParaTTRecursos.mutateAsync,
    isMovingToFinal: moverParaFinal.isPending,
    isRealizandoInput: realizarInput.isPending,
    isMovingToTTRecursos: moverParaTTRecursos.isPending,
  };
}
